import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { adminMlmAPI } from '../../services/api';
import { ArrowLeft, User, GitBranch, GitFork, Users, Activity } from 'lucide-react';

const BinarySubtree = ({ node, level = 0, isDarkMode }) => {
  if (!node) return null;
  return (
    <div className="select-none">
      <div className="flex items-center gap-2 py-1 px-2" style={{ paddingLeft: `${level * 20 + 8}px` }}>
        <div className="w-2 h-2 rounded-full bg-gray-400" />
        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{node.name || 'Empty'}</span>
        {node.position && (
          <span className={`text-xs font-medium ${node.position === 'left' ? 'text-blue-500' : 'text-red-500'}`}>
            [{node.position}]
          </span>
        )}
      </div>
      {node.left && <BinarySubtree node={node.left} level={level + 1} isDarkMode={isDarkMode} />}
      {node.right && <BinarySubtree node={node.right} level={level + 1} isDarkMode={isDarkMode} />}
    </div>
  );
};

const UnilevelSubtree = ({ node, isDarkMode }) => {
  if (!node) return null;
  const hasChildren = node.children && node.children.length > 0;
  return (
    <div className="select-none">
      <div className="flex items-center gap-2 py-1 px-2" style={{ paddingLeft: `${(node.level || 0) * 20 + 8}px` }}>
        <div className="w-2 h-2 rounded-full bg-orange-400" />
        <span className={`text-sm ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{node.name || 'Unknown'}</span>
        {node.rank && <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>({node.rank})</span>}
      </div>
      {hasChildren && node.children.map((child, i) => (
        <UnilevelSubtree key={child.id || i} node={child} isDarkMode={isDarkMode} />
      ))}
    </div>
  );
};

const UserNetworkView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminMlmAPI.getUserNetwork(userId)
      .then((res) => setNetwork(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const cardClass = `${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl p-6 shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className={`p-2 rounded-xl ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
        >
          <ArrowLeft className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        </button>
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>User Network</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>User ID: {userId}</p>
        </div>
      </div>

      {network?.user && (
        <div className={cardClass}>
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                {network.user.name || network.user.first_name || 'N/A'}
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                STTORA ID: {network.user.sttora_id || '—'} · Rank: {network.user.rank || '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={cardClass}>
          <div className="flex items-center gap-2 mb-4">
            <GitBranch className="w-5 h-5 text-blue-500" />
            <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Binary Tree</h3>
          </div>
          {network?.binary_tree?.root ? (
            <div className={`border rounded-xl p-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <BinarySubtree node={network.binary_tree.root} isDarkMode={isDarkMode} />
            </div>
          ) : (
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No binary tree data</p>
          )}
        </div>

        <div className={cardClass}>
          <div className="flex items-center gap-2 mb-4">
            <GitFork className="w-5 h-5 text-orange-500" />
            <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Unilevel Downline</h3>
          </div>
          {network?.unilevel_downline && network.unilevel_downline.length > 0 ? (
            <div className={`border rounded-xl p-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {network.unilevel_downline.map((node, i) => (
                <UnilevelSubtree key={node.id || i} node={node} isDarkMode={isDarkMode} />
              ))}
            </div>
          ) : (
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No unilevel data</p>
          )}
        </div>
      </div>

      {network?.centers && network.centers.length > 0 && (
        <div className={cardClass}>
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-green-500" />
            <h3 className={`font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>Centers</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Center ID</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Left Volume</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Right Volume</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Team Size</th>
                  <th className={`text-left py-3 px-4 font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Position</th>
                </tr>
              </thead>
              <tbody>
                {network.centers.map((c, i) => (
                  <tr key={c.id || i} className={`border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
                    <td className={`py-3 px-4 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{c.center_id || c.id}</td>
                    <td className="py-3 px-4 text-blue-500 font-medium">{c.left_volume || 0}</td>
                    <td className="py-3 px-4 text-red-500 font-medium">{c.right_volume || 0}</td>
                    <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.team_size || 0}</td>
                    <td className={`py-3 px-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.position || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default UserNetworkView;
